import React, { useEffect, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { ArrowBack, Error as ErrorIcon } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import moment from 'moment'
import NoResultsAnimation from '../../../components/Shared/animations/NoResultsAnimation'
import PaginationGlobal from '../../../components/Shared/PaginationGlobal'

const cellSx = {
    color: '#94A3B8',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E2E8F0',
}

const STATUS_MAP = {
    completed: { label: 'Concluído', color: '#22C55E' },
    processing: { label: 'Processando', color: '#3B82F6' },
    error: { label: 'Erro', color: '#EF4444' },
}

const ImportHistory = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    const [response, setResponse] = useState(null)
    const [filters, setFilters] = useState({ page_size: 50, page: 1 })
    const [undoing, setUndoing] = useState(null)
    const [confirmDialog, setConfirmDialog] = useState(null)

    const fetchImports = () => {
        api.get('cashflow/statement-imports/', {
            params: {
                account_id: id,
                condo_id: getCondoId(),
                page: filters.page,
                page_size: filters.page_size,
            },
        })
            .then((res) => setResponse(res.data))
            .catch(() => {
                enqueueSnackbar('Erro ao carregar importações', { variant: 'error' })
                setResponse({ results: [], count: 0 })
            })
    }

    useEffect(() => {
        window.scrollTo(0, 0)
        fetchImports()
    }, [filters])

    const handleUndo = (imp) => {
        setConfirmDialog(imp)
    }

    const confirmUndo = () => {
        const imp = confirmDialog
        setConfirmDialog(null)
        setUndoing(imp.id)

        api.delete(`cashflow/statement-imports/${imp.id}/undo/`)
            .then((res) => {
                const deleted = res.data?.deleted_entries || 0
                enqueueSnackbar(`Importação desfeita! ${deleted} lançamentos removidos.`, {
                    variant: 'success',
                })
                fetchImports()
            })
            .catch(() => enqueueSnackbar('Erro ao desfazer importação', { variant: 'error' }))
            .finally(() => setUndoing(null))
    }

    const getStatusChip = (status) => {
        const config = STATUS_MAP[status] || STATUS_MAP.error
        return (
            <Chip
                label={config.label}
                size="small"
                icon={
                    status === 'processing' ? (
                        <CircularProgress size={14} sx={{ color: config.color }} />
                    ) : undefined
                }
                sx={{
                    bgcolor: `${config.color}14`,
                    color: config.color,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 24,
                    border: `1px solid ${config.color}30`,
                }}
            />
        )
    }

    return (
        <>
            <Helmet>
                <title>Histórico de Importações - Bloco B3</title>
            </Helmet>

            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="xl" sx={{ py: 3 }}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={3}
                    >
                        <Box>
                            <Button
                                startIcon={<ArrowBack />}
                                onClick={() =>
                                    navigate(`/financeiro/contas-bancarias/${id}/extrato`)
                                }
                                sx={{ textTransform: 'none', color: '#64748B', mb: 0.5 }}
                            >
                                Voltar ao Extrato
                            </Button>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                Histórico de Importações
                            </Typography>
                        </Box>
                    </Box>

                    <Card
                        sx={{
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        {response?.results ? (
                            <TableContainer>
                                {response.results.length > 0 ? (
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                                <TableCell sx={cellSx}>Data</TableCell>
                                                <TableCell sx={cellSx}>Conta</TableCell>
                                                <TableCell sx={cellSx}>Formato</TableCell>
                                                <TableCell align="right" sx={cellSx}>
                                                    Novos
                                                </TableCell>
                                                <TableCell align="right" sx={cellSx}>
                                                    Duplicatas
                                                </TableCell>
                                                <TableCell align="center" sx={cellSx}>
                                                    Status
                                                </TableCell>
                                                <TableCell sx={cellSx}>Importado por</TableCell>
                                                <TableCell align="center" sx={cellSx}>
                                                    Ações
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {response.results.map((imp) => (
                                                <TableRow
                                                    key={imp.id}
                                                    sx={{
                                                        '&:hover': { bgcolor: '#F1F5F9' },
                                                        '& td': {
                                                            borderBottom: '1px solid #F1F5F9',
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ color: '#64748B', fontSize: '0.85rem' }}
                                                        >
                                                            {moment(imp.created_at).format(
                                                                'DD/MM/YYYY HH:mm'
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#1E293B',
                                                                fontWeight: 500,
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {imp.account_name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={imp.file_format?.toUpperCase()}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#F1F5F9',
                                                                color: '#64748B',
                                                                fontWeight: 600,
                                                                fontSize: '0.7rem',
                                                                height: 22,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: '#1E293B',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {imp.entries_created}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#94A3B8',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {imp.entries_skipped}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {imp.status === 'error' ? (
                                                            <Tooltip title={imp.error_message || 'Erro'}>
                                                                {getStatusChip(imp.status)}
                                                            </Tooltip>
                                                        ) : (
                                                            getStatusChip(imp.status)
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#64748B',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {imp.imported_by_name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {imp.status === 'completed' && (
                                                            <LoadingButton
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                loading={undoing === imp.id}
                                                                onClick={() => handleUndo(imp)}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 600,
                                                                    borderRadius: 1.5,
                                                                }}
                                                            >
                                                                Desfazer
                                                            </LoadingButton>
                                                        )}
                                                        {imp.status === 'error' && (
                                                            <Tooltip title={imp.error_message}>
                                                                <ErrorIcon
                                                                    sx={{ fontSize: 20, color: '#EF4444' }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Box sx={{ py: 6 }}>
                                        <NoResultsAnimation />
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#94A3B8', textAlign: 'center', mt: 1 }}
                                        >
                                            Nenhuma importação encontrada.
                                        </Typography>
                                    </Box>
                                )}
                            </TableContainer>
                        ) : (
                            <Skeleton
                                variant="rounded"
                                height={400}
                                animation="pulse"
                                sx={{ m: 2 }}
                            />
                        )}
                    </Card>

                    {response?.count > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <PaginationGlobal
                                state={response}
                                filters={filters}
                                setFilters={setFilters}
                            />
                        </Box>
                    )}
                </Container>
            </Box>

            {/* Confirm undo dialog */}
            <Dialog
                open={!!confirmDialog}
                onClose={() => setConfirmDialog(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Desfazer Importação</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza? Isto removerá{' '}
                        <strong>{confirmDialog?.entries_created}</strong> lançamentos e suas
                        conciliações.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setConfirmDialog(null)}
                        sx={{ textTransform: 'none', color: '#64748B' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmUndo}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                    >
                        Desfazer
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ImportHistory
